import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import { useEffect, useCallback } from 'react'
import { tokens } from '@fluentui/react-components'

interface EmailHtmlEditorProps {
  value: string
  onChange: (html: string) => void
}

const btnStyle = (active = false): React.CSSProperties => ({
  padding: '4px 10px',
  fontSize: '13px',
  fontFamily: 'inherit',
  border: `1px solid ${active ? tokens.colorBrandStroke1 : tokens.colorNeutralStroke2}`,
  borderRadius: '4px',
  background: active ? tokens.colorBrandBackground2 : tokens.colorNeutralBackground1,
  color: active ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground1,
  cursor: 'pointer',
  fontWeight: active ? 600 : 400,
  lineHeight: 1,
})

export default function EmailHtmlEditor({ value, onChange }: EmailHtmlEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer' } }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
  })

  // Synchronizuj hodnotu zvenčí (při resetu nebo načtení z DB)
  useEffect(() => {
    if (!editor) return
    const current = editor.getHTML()
    if (value !== current) {
      editor.commands.setContent(value || '')
    }
  }, [value, editor])

  const setLink = useCallback(() => {
    if (!editor) return
    const prev = editor.getAttributes('link').href ?? ''
    const url = window.prompt('URL odkazu:', prev)
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }
  }, [editor])

  if (!editor) return null

  return (
    <div style={{ border: `1.5px solid ${tokens.colorNeutralStroke1}`, borderRadius: tokens.borderRadiusMedium, overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', gap: '4px', padding: '6px 8px',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        background: tokens.colorNeutralBackground2,
        flexWrap: 'wrap',
      }}>
        <button
          style={btnStyle(editor.isActive('bold'))}
          onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleBold().run() }}
          title="Tučně (Ctrl+B)"
        >
          <strong>B</strong>
        </button>
        <button
          style={btnStyle(editor.isActive('italic'))}
          onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleItalic().run() }}
          title="Kurzíva (Ctrl+I)"
        >
          <em>I</em>
        </button>
        <div style={{ width: 1, background: tokens.colorNeutralStroke2, margin: '0 2px' }} />
        <button
          style={btnStyle(editor.isActive('paragraph'))}
          onMouseDown={e => { e.preventDefault(); editor.chain().focus().setParagraph().run() }}
          title="Odstavec"
        >
          ¶
        </button>
        <button
          style={btnStyle(editor.isActive('heading', { level: 2 }))}
          onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 2 }).run() }}
          title="Nadpis"
        >
          H2
        </button>
        <div style={{ width: 1, background: tokens.colorNeutralStroke2, margin: '0 2px' }} />
        <button
          style={btnStyle(editor.isActive('link'))}
          onMouseDown={e => { e.preventDefault(); setLink() }}
          title="Vložit odkaz"
        >
          🔗
        </button>
        <button
          style={{ ...btnStyle(), color: tokens.colorPaletteRedForeground1 }}
          onMouseDown={e => { e.preventDefault(); editor.chain().focus().unsetLink().run() }}
          title="Odebrat odkaz"
          disabled={!editor.isActive('link')}
        >
          ✂
        </button>
        <div style={{ width: 1, background: tokens.colorNeutralStroke2, margin: '0 2px' }} />
        <button
          style={btnStyle()}
          onMouseDown={e => { e.preventDefault(); editor.chain().focus().undo().run() }}
          title="Zpět (Ctrl+Z)"
          disabled={!editor.can().undo()}
        >
          ↩
        </button>
        <button
          style={btnStyle()}
          onMouseDown={e => { e.preventDefault(); editor.chain().focus().redo().run() }}
          title="Znovu (Ctrl+Shift+Z)"
          disabled={!editor.can().redo()}
        >
          ↪
        </button>
      </div>

      {/* Editor plocha */}
      <EditorContent
        editor={editor}
        style={{ minHeight: '140px', padding: '10px 14px', fontSize: '13px', lineHeight: '1.6', cursor: 'text' }}
      />
    </div>
  )
}
